import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Undo2, Check, Plus, ArrowLeft } from "lucide-react";
import { api, apiError, gbp, num, dateTime } from "@/lib/api";
import { AppShell, PageHeader, Panel, EmptyState, SkeletonRows, Btn, Tag } from "@/components/Shell";

const STATUS_TONE = { open: "amber", posted: "green", reversed: "slate" };

export default function Stocktakes() {
  const [sessions, setSessions] = useState(null);
  const [active, setActive] = useState(null);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ name: "", sku_prefix: "", location_id: "", blanks_as_zero: false });
  const [busy, setBusy] = useState("");

  const loadSessions = async () => {
    const { data } = await api.get("/stocktakes");
    setSessions(data);
  };

  useEffect(() => {
    loadSessions().catch((e) => toast.error(apiError(e)));
    api.get("/locations").then((r) => {
      setLocations(r.data);
      setForm((f) => ({ ...f, location_id: f.location_id || r.data[0]?.id || "" }));
    }).catch(() => {});
  }, []);

  const open = async (id) => {
    try {
      const { data } = await api.get(`/stocktakes/${id}`);
      setActive(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const create = async () => {
    setBusy("create");
    try {
      const { data } = await api.post("/stocktakes", {
        name: form.name || `Count ${new Date().toLocaleDateString("en-GB")}`,
        location_id: form.location_id || null,
        sku_prefix: form.sku_prefix,
        blanks_as_zero: form.blanks_as_zero,
      });
      toast.success(`Session started — ${data.lines} lines to count`);
      setForm((f) => ({ ...f, name: "", sku_prefix: "" }));
      await loadSessions();
      await open(data.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const saveCount = async (line, value, note) => {
    const counted = value === "" ? null : Number(value);
    try {
      const { data } = await api.put(`/stocktakes/${active.id}/count`, {
        product_id: line.product_id,
        counted,
        note: note ?? line.note ?? "",
      });
      setActive(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const post = async () => {
    const uncounted = active.line_count - active.counted_count;
    if (active.blanks_as_zero && uncounted > 0) {
      const ok = window.confirm(
        `${uncounted} line(s) were never counted and will be written down to zero because "treat uncounted lines as zero" is on. Post anyway?`
      );
      if (!ok) return;
    }
    setBusy("post");
    try {
      const { data } = await api.post(`/stocktakes/${active.id}/post`);
      toast.success(`${data.posted} variance(s) posted · net ${gbp(data.value_pence)} journalled to Xero`);
      await loadSessions();
      await open(active.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const reverse = async (session) => {
    try {
      await api.post(`/stocktakes/${session.id}/reverse`);
      toast.success("Stocktake reversed");
      await loadSessions();
      if (active?.id === session.id) await open(session.id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (active) {
    const counted = active.counted_count;
    return (
      <AppShell>
        <button onClick={() => setActive(null)} className="mb-3 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="back-to-stocktakes">
          <ArrowLeft className="h-3.5 w-3.5" /> All stocktakes
        </button>
        <PageHeader
          title={active.name}
          subtitle={`${active.sku_prefix ? `Shelf ${active.sku_prefix}* · ` : "Whole location · "}${num(counted)} of ${num(active.line_count)} lines counted${active.blanks_as_zero ? " · blanks count as zero" : " · blanks ignored"}`}
          testId="stocktake-detail-header"
          actions={
            <>
              <Tag tone={STATUS_TONE[active.status]} testId="stocktake-status">{active.status}</Tag>
              {active.status === "open" && (
                <Btn data-testid="stocktake-post-button" disabled={busy === "post" || active.variance_count === 0} onClick={post}>
                  <Check className="h-4 w-4" />
                  {busy === "post" ? "Posting…" : `Post ${num(active.variance_count)} difference(s)`}
                </Btn>
              )}
              {active.can_reverse && (
                <Btn variant="danger" data-testid="stocktake-reverse-button" onClick={() => reverse(active)}>
                  <Undo2 className="h-4 w-4" /> Undo posting
                </Btn>
              )}
            </>
          }
        />

        <div className="mb-4 grid gap-4 sm:grid-cols-3">
          <div className="border border-slate-200 bg-white p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Lines counted</p>
            <p className="num mt-1 text-2xl" data-testid="stocktake-counted">{num(counted)} / {num(active.line_count)}</p>
          </div>
          <div className="border border-slate-200 bg-white p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Differences</p>
            <p className="num mt-1 text-2xl text-amber-600" data-testid="stocktake-variance-count">{num(active.variance_count)}</p>
          </div>
          <div className="border border-slate-200 bg-white p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Net value</p>
            <p className="num mt-1 text-2xl" data-testid="stocktake-variance-value">{gbp(active.variance_value_pence)}</p>
          </div>
        </div>

        <Panel title="Count sheet" testId="stocktake-lines-panel">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-right">System</th>
                  <th className="px-3 py-2 text-right">Counted</th>
                  <th className="px-3 py-2 text-right">Difference</th>
                  <th className="px-3 py-2 text-left">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {active.lines.map((line) => {
                  const diff = line.counted === null || line.counted === undefined
                    ? null
                    : line.counted - line.expected;
                  return (
                    <tr key={line.product_id} className={diff ? "bg-amber-50/50" : ""} data-testid={`stocktake-line-${line.sku}`}>
                      <td className="mono px-4 py-2">{line.sku}</td>
                      <td className="px-3 py-2 text-slate-700">{line.name}</td>
                      <td className="num px-3 py-2 text-right text-slate-500">{num(line.expected)}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          data-testid={`stocktake-count-input-${line.sku}`}
                          type="number"
                          min="0"
                          disabled={active.status !== "open"}
                          defaultValue={line.counted ?? ""}
                          onBlur={(e) => {
                            const v = e.target.value;
                            const current = line.counted === null || line.counted === undefined ? "" : String(line.counted);
                            if (v !== current) saveCount(line, v);
                          }}
                          className="mono w-24 border border-slate-300 px-2 py-2 text-right text-sm focus:border-slate-900 focus:outline-none disabled:bg-slate-50"
                        />
                      </td>
                      <td className="num px-3 py-2 text-right" style={{ color: !diff ? "#94A3B8" : diff > 0 ? "#10B981" : "#EF4444" }}
                          data-testid={`stocktake-diff-${line.sku}`}>
                        {diff === null ? "—" : diff > 0 ? `+${diff}` : diff}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          data-testid={`stocktake-note-input-${line.sku}`}
                          disabled={active.status !== "open"}
                          defaultValue={line.note || ""}
                          onBlur={(e) => {
                            if ((e.target.value || "") !== (line.note || "")) saveCount(line, line.counted ?? "", e.target.value);
                          }}
                          placeholder="Optional note"
                          className="w-full border border-slate-200 px-2 py-2 text-xs focus:border-slate-900 focus:outline-none disabled:bg-slate-50"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageHeader
        title="Stocktakes"
        subtitle="Count a whole location or one shelf at a time on a tablet, then post every difference in one go as stocktake adjustments journalled to Xero."
        testId="stocktakes-header"
      />

      <div className="grid gap-4 xl:grid-cols-12">
        <Panel title="Start a session" testId="stocktake-form-panel" className="xl:col-span-4">
          <div className="space-y-3 p-4">
            <label className="block text-sm">
              Session name
              <input
                data-testid="stocktake-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Aisle 3 · Friday count"
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              />
            </label>
            <label className="block text-sm">
              Location
              <select
                data-testid="stocktake-location-select"
                value={form.location_id}
                onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                className="mt-1 w-full border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              >
                {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
              </select>
            </label>
            <label className="block text-sm">
              Shelf / SKU prefix <span className="text-slate-400">(blank = whole location)</span>
              <input
                data-testid="stocktake-prefix-input"
                value={form.sku_prefix}
                onChange={(e) => setForm({ ...form, sku_prefix: e.target.value.toUpperCase() })}
                placeholder="KTL"
                className="mono mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="stocktake-blanks-toggle"
                checked={form.blanks_as_zero}
                onChange={(e) => setForm({ ...form, blanks_as_zero: e.target.checked })}
              />
              Treat uncounted lines as zero
            </label>
            <p className="text-xs text-slate-500">
              Off by default — only the lines your team types are posted, so a half-finished count
              never writes stock off.
            </p>
            <Btn className="w-full justify-center" data-testid="stocktake-create-button" disabled={busy === "create"} onClick={create}>
              <Plus className="h-4 w-4" /> {busy === "create" ? "Starting…" : "Start counting"}
            </Btn>
          </div>
        </Panel>

        <Panel title="Sessions" testId="stocktake-list-panel" className="xl:col-span-8">
          {!sessions ? (
            <SkeletonRows rows={4} />
          ) : sessions.length === 0 ? (
            <EmptyState icon={ClipboardList} title="No stocktakes yet" body="Start a session to count a shelf or a whole location." testId="stocktakes-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Session</th>
                    <th className="px-3 py-2 text-left">Scope</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Counted</th>
                    <th className="px-3 py-2 text-right">Differences</th>
                    <th className="px-3 py-2 text-right">Net value</th>
                    <th className="px-3 py-2 text-left">Started</th>
                    <th className="px-3 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sessions.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-50" data-testid={`stocktake-row-${s.id}`}>
                      <td className="px-4 py-2">
                        <button onClick={() => open(s.id)} className="text-slate-900 underline-offset-2 hover:underline" data-testid={`stocktake-open-${s.id}`}>
                          {s.name}
                        </button>
                      </td>
                      <td className="mono px-3 py-2 text-xs text-slate-500">{s.sku_prefix ? `${s.sku_prefix}*` : "all SKUs"}</td>
                      <td className="px-3 py-2"><Tag tone={STATUS_TONE[s.status]}>{s.status}</Tag></td>
                      <td className="num px-3 py-2 text-right">{num(s.counted_count)} / {num(s.line_count)}</td>
                      <td className="num px-3 py-2 text-right text-amber-600">{num(s.variance_count)}</td>
                      <td className="num px-3 py-2 text-right">{gbp(s.variance_value_pence)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{dateTime(s.created_at)}</td>
                      <td className="px-3 py-2 text-right">
                        {s.can_reverse && (
                          <Btn variant="danger" className="px-2 py-1 text-xs" data-testid={`stocktake-undo-${s.id}`} onClick={() => reverse(s)}>
                            <Undo2 className="h-3 w-3" /> Undo
                          </Btn>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </AppShell>
  );
}
